import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';

// ImageCropDialog is the reusable crop modal. The component:
//
//  1. Loads the original image from the `src` URL.
//  2. Lets the user drag a rectangle (with optional fixed aspect) over
//     the image to pick the crop region.
//  3. On confirm, draws the chosen region to a hidden <canvas>, calls
//     canvas.toBlob to produce a fresh JPEG/PNG/WebP, then calls the
//     `onCrop(file)` callback with the resulting File. Callers own
//     the upload + state-update step so the dialog stays domain-
//     agnostic (org media, broadcast image, member photo, etc.).
//
// Earlier revisions of this component knew about the media-upload
// endpoint directly. Switching to a callback-based contract is what
// lets MemberCard's photo flow (which uploads to a member-keyed
// endpoint, not a generic /media path) consume the same cropper.

export interface ImageCropDialogProps {
  open: boolean;
  src: string;
  // Optional fixed aspect ratio. When set, the crop rectangle stays at
  // that ratio (resizing one corner moves the opposite axis to match).
  // Pass undefined for free-form. The picker UI also lets the user
  // toggle between this prop's value and "Free" — but never to a
  // different fixed ratio than the caller asked for.
  aspect?: number;
  // filename is the base name written into the resulting File. Defaults
  // to "crop" — uploaders that store the original name (e.g. media
  // service does) typically pass the original key so the cropped file
  // ends up as "<orig>-crop.<ext>".
  filename?: string;
  // onCrop receives the cropped File. Resolve when upload + state
  // update are complete; reject (throw) to surface an error in the
  // dialog without auto-closing. The dialog stays open while the
  // promise is pending so a slow upload keeps the busy state visible.
  onCrop: (file: File) => Promise<void>;
  onClose: () => void;
}

// Local crop rectangle is in DISPLAYED-image coordinates (0..1 fraction
// of the rendered <img>). We convert to source-pixel coordinates at
// canvas-draw time using the natural-vs-displayed scale.
interface Rect {
  x: number; // [0..1]
  y: number;
  w: number;
  h: number;
}

const DEFAULT_RECT: Rect = { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };

export function ImageCropDialog({
  open,
  src,
  aspect,
  filename,
  onCrop,
  onClose,
}: ImageCropDialogProps) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [rect, setRect] = useState<Rect>(() => fitAspect(DEFAULT_RECT, aspect));
  const [keepAspect, setKeepAspect] = useState(aspect !== undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Displayed image size in CSS pixels, computed from the natural size
  // and the available stage area. Small source images (e.g. a club
  // logo) would otherwise render at their native pixel size — far too
  // small to drag a crop over — so we scale UP to fill the stage as
  // well as down. The stage shrink-wraps the <img>, so the crop rect's
  // [0..1] coordinate space stays mapped to the rendered image bounds.
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const computeDims = () => {
    const img = imgRef.current;
    const host = containerRef.current?.parentElement;
    if (!img || !img.naturalWidth || !img.naturalHeight || !host) return;
    const availW = host.clientWidth;
    const availH = window.innerHeight * 0.7;
    const scale = Math.min(availW / img.naturalWidth, availH / img.naturalHeight);
    setDims({ w: img.naturalWidth * scale, h: img.naturalHeight * scale });
  };

  // Recompute the displayed size when the viewport changes (e.g. device
  // rotation) so the stage keeps filling the available space.
  useEffect(() => {
    if (!open) return;
    const onResize = () => computeDims();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // Re-fit when the prop aspect changes (e.g. caller opens for a
  // different field) or when the user toggles keepAspect.
  useEffect(() => {
    setRect((r) => (keepAspect && aspect ? fitAspect(r, aspect) : r));
  }, [keepAspect, aspect]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setRect(fitAspect(DEFAULT_RECT, keepAspect ? aspect : undefined));
      setErr(null);
      setLoaded(false);
      setDims(null);
      // Cached <img> may skip onLoad on the second open. Flip loaded
      // true on the next frame if the browser reports complete.
      requestAnimationFrame(() => {
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
          setLoaded(true);
          computeDims();
        }
      });
    }
    if (!open && d.open) d.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const memoSrc = useMemo(() => src, [src]);

  const onConfirm = async () => {
    setErr(null);
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      setErr('notLoaded');
      return;
    }
    setBusy(true);
    try {
      const blob = await cropToBlob(img, rect, img.naturalWidth >= 2000 ? 0.85 : 0.92);
      if (!blob) {
        setErr('encode');
        setBusy(false);
        return;
      }
      const file = new File([blob], inferFilename(filename, blob.type), { type: blob.type });
      await onCrop(file);
      onClose();
    } catch (e) {
      setErr(e instanceof Error && e.message ? e.message : 'upload_failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="schedule-dialog image-crop-dialog"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="schedule-dialog__inner" onClick={(e) => e.stopPropagation()}>
        <h3>{t('imageCrop.title')}</h3>
        <p>{t('imageCrop.body')}</p>

        {/* The crop rect dims everything outside it via a huge box-shadow;
            overflow:hidden clips that dimming to the image so it can't
            bleed over the footer buttons. The stage is sized to the image
            so the rect's [0..1] coordinate space maps cleanly. */}
        <div
          ref={containerRef}
          className="image-crop-dialog__stage"
          style={{ position: 'relative', userSelect: 'none', overflow: 'hidden' }}
        >
          <img
            ref={imgRef}
            src={memoSrc}
            alt=""
            draggable={false}
            crossOrigin="anonymous"
            onLoad={() => {
              setLoaded(true);
              computeDims();
            }}
            style={
              dims
                ? { width: dims.w, height: dims.h, display: 'block' }
                : { maxWidth: '100%', maxHeight: '70vh', display: 'block' }
            }
          />
          {loaded && (
            <CropRect
              rect={rect}
              {...(keepAspect && aspect !== undefined ? { aspect } : {})}
              onChange={setRect}
              container={containerRef.current}
            />
          )}
        </div>

        {aspect !== undefined && (
          <label className="image-crop-dialog__keep">
            <Checkbox
              checked={keepAspect}
              onChange={(e) => setKeepAspect(e.target.checked)}
            />
            {t('imageCrop.lockAspect', { aspect: aspect.toFixed(2) })}
          </label>
        )}

        {err && (
          <p className="schedule-dialog__error" role="alert">
            {t(`imageCrop.error.${err}`, {
              defaultValue: t('imageCrop.error.unknown'),
            })}
          </p>
        )}

        <footer className="schedule-dialog__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !loaded}
            onClick={() => void onConfirm()}
          >
            {busy ? t('common.submitting') : t('imageCrop.confirm')}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

// CropRect renders the draggable rectangle + 8 handles over the image.
// Coordinates are in [0..1] fractions of the container; the parent
// converts to natural pixels at draw time.
function CropRect({
  rect,
  aspect,
  onChange,
  container,
}: {
  rect: Rect;
  aspect?: number;
  onChange: (r: Rect) => void;
  container: HTMLDivElement | null;
}) {
  // Hands a pointerdown handler that fires drag-move-resize logic. The
  // movement target is one of: the whole rect, a corner handle, or an
  // edge. All math runs in fractional [0..1] coords so re-rendering at
  // a different display size doesn't shift the selection.
  const start = (mode: DragMode) => (ev: React.PointerEvent) => {
    if (!container) return;
    ev.preventDefault();
    // Handles live inside the draggable rect <div>, which also listens
    // for pointerdown ('move'). Without this, grabbing a handle fires
    // BOTH the resize and the move handler, and the move (computed from
    // the same pointer delta) overwrites the resize on every frame — so
    // the crop never resizes. Stop the bubble so only this mode runs.
    ev.stopPropagation();
    (ev.target as Element).setPointerCapture(ev.pointerId);
    const bb = container.getBoundingClientRect();
    const startMouse = { x: (ev.clientX - bb.left) / bb.width, y: (ev.clientY - bb.top) / bb.height };
    const startRect = { ...rect };
    const move = (e: PointerEvent) => {
      const mx = clamp01((e.clientX - bb.left) / bb.width);
      const my = clamp01((e.clientY - bb.top) / bb.height);
      onChange(applyDrag(mode, startRect, startMouse, { x: mx, y: my }, aspect));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const style = {
    position: 'absolute' as const,
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
    border: '2px solid #BEE034',
    boxShadow: '0 0 0 100vmax rgba(0,0,0,0.4)',
    cursor: 'move',
  };
  const handleStyle = (extra: React.CSSProperties): React.CSSProperties => ({
    position: 'absolute',
    width: 12,
    height: 12,
    background: '#fff',
    border: '2px solid #26272B',
    borderRadius: 9999,
    ...extra,
  });

  return (
    <div style={style} onPointerDown={start('move')}>
      <span style={handleStyle({ left: -8, top: -8, cursor: 'nwse-resize' })} onPointerDown={start('nw')} />
      <span style={handleStyle({ right: -8, top: -8, cursor: 'nesw-resize' })} onPointerDown={start('ne')} />
      <span style={handleStyle({ left: -8, bottom: -8, cursor: 'nesw-resize' })} onPointerDown={start('sw')} />
      <span style={handleStyle({ right: -8, bottom: -8, cursor: 'nwse-resize' })} onPointerDown={start('se')} />
      <span style={handleStyle({ left: 'calc(50% - 6px)', top: -8, cursor: 'ns-resize' })} onPointerDown={start('n')} />
      <span style={handleStyle({ left: 'calc(50% - 6px)', bottom: -8, cursor: 'ns-resize' })} onPointerDown={start('s')} />
      <span style={handleStyle({ left: -8, top: 'calc(50% - 6px)', cursor: 'ew-resize' })} onPointerDown={start('w')} />
      <span style={handleStyle({ right: -8, top: 'calc(50% - 6px)', cursor: 'ew-resize' })} onPointerDown={start('e')} />
    </div>
  );
}

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';

function applyDrag(
  mode: DragMode,
  start: Rect,
  startMouse: { x: number; y: number },
  mouse: { x: number; y: number },
  aspect: number | undefined,
): Rect {
  const dx = mouse.x - startMouse.x;
  const dy = mouse.y - startMouse.y;
  if (mode === 'move') {
    return {
      x: clamp01(start.x + dx, 1 - start.w),
      y: clamp01(start.y + dy, 1 - start.h),
      w: start.w,
      h: start.h,
    };
  }
  let left = start.x, top = start.y, right = start.x + start.w, bottom = start.y + start.h;
  switch (mode) {
    case 'nw': left = mouse.x; top = mouse.y; break;
    case 'ne': right = mouse.x; top = mouse.y; break;
    case 'sw': left = mouse.x; bottom = mouse.y; break;
    case 'se': right = mouse.x; bottom = mouse.y; break;
    case 'n': top = mouse.y; break;
    case 's': bottom = mouse.y; break;
    case 'w': left = mouse.x; break;
    case 'e': right = mouse.x; break;
  }
  let x = Math.min(left, right);
  let y = Math.min(top, bottom);
  let w = Math.max(0.05, Math.abs(right - left));
  let h = Math.max(0.05, Math.abs(bottom - top));
  if (aspect && aspect > 0) {
    if (mode === 'n' || mode === 's') {
      // Vertical edge: height drives the size, width follows, and the
      // box stays centered on its original horizontal midpoint.
      w = h * aspect;
      if (w > 1) { w = 1; h = w / aspect; }
      x = clamp01(start.x + start.w / 2 - w / 2, 1 - w);
    } else {
      // Corners + horizontal edges: width drives, height follows.
      h = w / aspect;
      if (h > 1) { h = 1; w = h * aspect; }
      if (mode === 'e' || mode === 'w') {
        y = clamp01(start.y + start.h / 2 - h / 2, 1 - h);
      }
    }
  }
  if (x + w > 1) w = 1 - x;
  if (y + h > 1) h = 1 - y;
  return { x, y, w, h };
}

function clamp01(v: number, max: number = 1): number {
  return Math.min(max, Math.max(0, v));
}

function fitAspect(r: Rect, aspect: number | undefined): Rect {
  if (!aspect || aspect <= 0) return r;
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  let w = r.w;
  let h = w / aspect;
  if (h > r.h) {
    h = r.h;
    w = h * aspect;
  }
  return {
    x: clamp01(cx - w / 2, 1 - w),
    y: clamp01(cy - h / 2, 1 - h),
    w,
    h,
  };
}

async function cropToBlob(img: HTMLImageElement, rect: Rect, quality: number): Promise<Blob | null> {
  const sx = Math.round(rect.x * img.naturalWidth);
  const sy = Math.round(rect.y * img.naturalHeight);
  const sw = Math.round(rect.w * img.naturalWidth);
  const sh = Math.round(rect.h * img.naturalHeight);
  if (sw < 4 || sh < 4) return null;
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  const type = inferOutputType(img.src);
  return await new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function inferOutputType(src: string): string {
  const lower = src.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

function inferFilename(filename: string | undefined, mime: string): string {
  const base = (filename ?? 'crop').replace(/^.*\//, '').replace(/\.[^.]+$/, '') || 'crop';
  const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg';
  return `${base}-crop.${ext}`;
}
